import React, { useState } from "react";
import { Upload } from "lucide-react";
import { AppButton, Callout, FieldGroup, FileUpload, FormField, Input, Radio } from "../ui";
import { uploadSessionDocument, uploadSessionScorm } from "../../utils/smartLearning";
import { CONTENT_TYPES } from "./resourceKinds";

/**
 * Upload a new document — a video, PDF, slides, a document, or a SCORM package
 * — and hand it back to be attached. Non-SCORM files become a course-level
 * BatchMaterial; SCORM goes through its own pipeline (unzipped in the
 * background, playable once extracted).
 *
 * Props: courseId, allowScorm (SCORM upload is admin-only), onCreated, onCancel
 */
const ContentUploadForm = ({ courseId, allowScorm = true, onCreated, onCancel }) => {
  const types = allowScorm ? CONTENT_TYPES : CONTENT_TYPES.filter((t) => t.key !== "scorm");
  const [type, setType] = useState("pdf");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const cfg = types.find((t) => t.key === type) || types[0];
  const isScorm = type === "scorm";

  const pickType = (key) => {
    if (key === type) return;
    setType(key);
    setFile(null);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim()) { setError("Give it a title students will recognise."); return; }
    if (!file) { setError("Choose the file to upload."); return; }
    setSaving(true);
    setProgress(0);
    try {
      if (isScorm) {
        const picked = await uploadSessionScorm(
          { courseId, title: title.trim(), file },
          { onUploadProgress: (ev) => { if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100)); } },
        );
        if (!picked?._id) throw new Error("The upload did not return a SCORM package.");
        onCreated({ _id: picked._id, title: picked.title, kind: "scorm" });
      } else {
        const picked = await uploadSessionDocument({ courseId, title: title.trim(), file });
        if (!picked?._id) throw new Error("The upload did not return a document.");
        onCreated({ _id: picked._id, title: picked.title, kind: "document", contentType: type });
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "The upload failed. Try again.");
      setSaving(false);
      setProgress(0);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-4">
      <FieldGroup legend="What are you uploading?">
        <div className="tw-grid tw-grid-cols-2 tw-gap-2 min-[576px]:tw-grid-cols-3">
          {types.map(({ key, label, Icon }) => (
            <Radio
              key={key}
              variant="card"
              name="resource-content-type"
              value={key}
              checked={type === key}
              onChange={() => pickType(key)}
              disabled={saving}
              label={(
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-font-medium">
                  <Icon size={15} aria-hidden="true" className="tw-text-primary" /> {label}
                </span>
              )}
            />
          ))}
        </div>
      </FieldGroup>

      <FormField label="Title" required>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Loops — handout" disabled={saving} maxLength={200} />
      </FormField>

      <FileUpload
        key={type}
        files={file ? [file] : []}
        onChange={(fs) => setFile(fs[fs.length - 1] || null)}
        multiple={false}
        maxFiles={1}
        maxSizeMB={cfg.maxMB}
        accept={cfg.accept}
        label={`${cfg.label} file`}
        hint={`${cfg.hint} — drop it here or browse`}
        disabled={saving}
      />

      {isScorm && (
        <p className="tw-m-0 tw-text-xs tw-text-content-muted">
          SCORM packages are unzipped in the background. It is attached straight away and plays once that finishes.
        </p>
      )}
      {saving && isScorm && progress > 0 && (
        <p className="tw-m-0 tw-text-xs tw-text-content-muted" aria-live="polite">Uploading… {progress}%</p>
      )}
      {error && <Callout tone="danger" role="alert">{error}</Callout>}

      <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
        <AppButton type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancel</AppButton>
        <AppButton type="submit" disabled={saving}>
          <Upload size={14} aria-hidden="true" /> {saving ? "Uploading…" : "Upload and attach"}
        </AppButton>
      </div>
    </form>
  );
};

export default ContentUploadForm;
